import React, { useEffect, useRef, useState } from "react";

import { CloseOutlined } from "@ant-design/icons";
import { Button, Input } from "antd";
import Modal from "antd/es/modal/Modal";
import { debounce } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import { CHAR_LIMITS } from "../../../../SampleSubmission/SubmissionForm/Constant";
import SampleDetailsAutocompleteInput from "../../../../SampleSubmission/SubmissionForm/SampleDetailsPage/SampleDetails/SampleDetailsAutocompleteInput";

import { initializeObjectWithDefaultValue, removeReplicateTag } from "../../../../SampleSubmission/SubmissionForm/helpers";

import { saveSampleIdEditHistory } from "../../../../../actions/reports";
import {
  getDropdownSuggestionsAndTests,
  getSampleFields,
  getSampleID,
} from "../../../../../actions/sampleSubmission";

import { ReactComponent as LoadingDots } from "../../../../../assets/images/common/LoadingDots.svg";
import { ReactComponent as ExclamationIcon } from "../../../../../assets/images/sample-submission/exclamationIcon.svg";

import "./EditSampleIdModal.css";

const PRODUCT_SAMPLE_CATEGORY = "product";

function buildSampleFieldsInfo(fields) {
  const fieldsMap = {};
  fields.forEach((fieldObject) => {
    fieldsMap[fieldObject.json_field] = fieldObject;
  });
  return fieldsMap;
}

function getLatestSampleIdEditHistoryEntry(sampleIdEditHistory) {
  if (!sampleIdEditHistory.length) {
    return null;
  }

  // GET /sampleidedithistory/ always returns newest first
  return sampleIdEditHistory[0];
}

function buildPrefillValues(sampleIdFieldList, reportData, sampleIdEditHistory) {
  const values = {};
  const latestCustomizedFields = getLatestSampleIdEditHistoryEntry(sampleIdEditHistory)?.customized_fields || {};

  sampleIdFieldList.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(latestCustomizedFields, field)) {
      values[field] = latestCustomizedFields[field];
      return;
    }

    values[field] = reportData[field] || "";
  });
  return values;
}

function buildCustomizedFieldsSnapshot(sampleIdFieldList, inputValues) {
  const customizedFields = {};
  sampleIdFieldList.forEach((field) => {
    customizedFields[field] = removeReplicateTag((inputValues[field] || "").trim());
  });
  return customizedFields;
}

function sanitizeDropdown(preArray, returnArrayOfObjects = false) {
  const sanitizedArray = [];
  preArray.forEach((value) => {
    if (value !== null && value !== undefined && value !== "") {
      const tagRemoved = removeReplicateTag(value);
      if (tagRemoved.trim()) {
        if (returnArrayOfObjects) {
          sanitizedArray.push({ value: tagRemoved });
        } else {
          sanitizedArray.push(tagRemoved);
        }
      }
    }
  });
  return sanitizedArray;
}

export default function EditSampleIdModal({
  reportData,
  sampleIdEditHistory,
  displayedSampleId,
  onClose,
  onSaveSuccess,
}) {
  const [loadingFields, setLoadingFields] = useState(true);
  const [sampleFields, setSampleFields] = useState([]);
  const [sampleFieldsInfo, setSampleFieldsInfo] = useState({});
  const [sampleIdFields, setSampleIdFields] = useState(new Set());
  const [delimiterRegex, setDelimiterRegex] = useState("");
  const [inputValues, setInputValues] = useState({});
  const [inputInvalid, setInputInvalid] = useState({});
  const [dropdownSuggestions, setDropdownSuggestions] = useState({});
  const [generatedSampleId, setGeneratedSampleId] = useState("");
  const [sampleIdGenerationError, setSampleIdGenerationError] = useState(false);
  const [isLoadingSampleId, setIsLoadingSampleId] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const dropdownAbortControllerRef = useRef(null);
  const sampleIdAbortControllerRef = useRef(null);
  const inputValuesRefs = useRef({});
  const debouncedGenerateSampleIdRef = useRef(null);
  const debouncedFetchDropdownsRef = useRef(null);
  const sampleFieldListRef = useRef([]);

  const liveSampleId = reportData.sample_id;
  const receivedDate = reportData.received_date;
  const sampleType = reportData.sample_type || "Product";

  const setInputValuesRef = (field, inputElement) => {
    if (!inputValuesRefs.current[field]) {
      inputValuesRefs.current[field] = { ref: inputElement };
    } else {
      inputValuesRefs.current[field].ref = inputElement;
    }
  };

  const setFieldInputInvalid = (field, invalid) => {
    setInputInvalid((previousInvalid) => ({
      ...previousInvalid,
      [field]: invalid,
    }));
  };

  const buildSamplePayload = (currentInputValues, fieldList = sampleFieldListRef.current) => {
    const sample = {};

    const purchaseOrder = reportData.po;
    if (purchaseOrder !== undefined && purchaseOrder !== null && String(purchaseOrder).trim() !== "") {
      sample.po = removeReplicateTag(String(purchaseOrder).trim());
    }

    fieldList.forEach((field) => {
      sample[field] = removeReplicateTag((currentInputValues[field] || "").trim());
    });
    return sample;
  };

  const fetchGeneratedSampleId = async (currentInputValues, fieldList = sampleFieldListRef.current) => {
    if (!fieldList.length) {
      return { success: false, message: "Sample fields are not loaded" };
    }

    if (sampleIdAbortControllerRef.current) {
      sampleIdAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    sampleIdAbortControllerRef.current = abortController;
    setIsLoadingSampleId(true);

    try {
      const payload = {
        sample_category: PRODUCT_SAMPLE_CATEGORY,
        sample: buildSamplePayload(currentInputValues, fieldList),
        editing_sample_id: liveSampleId,
      };

      const response = await getSampleID(payload, abortController.signal);

      if (abortController.signal.aborted) {
        return { success: false, aborted: true };
      }

      if (response.success) {
        setGeneratedSampleId(response.sample_id);
        setSampleIdGenerationError(false);
        return { success: true, sample_id: response.sample_id };
      }

      setGeneratedSampleId("");
      setSampleIdGenerationError(true);
      return { success: false, message: response.message };
    } catch (error) {
      if (abortController.signal.aborted) {
        return { success: false, aborted: true };
      }

      setGeneratedSampleId("");
      setSampleIdGenerationError(true);
      return {
        success: false,
        message: error.message || "Failed to generate Sample ID",
      };
    } finally {
      // Only the active request clears loading; an aborted request leaves it for the newer one
      if (sampleIdAbortControllerRef.current === abortController) {
        sampleIdAbortControllerRef.current = null;
        setIsLoadingSampleId(false);
      }
    }
  };

  const fetchDropdownSuggestions = async (currentInputValues, fieldList = sampleFieldListRef.current) => {
    if (!fieldList.length) {
      return;
    }

    if (dropdownAbortControllerRef.current) {
      dropdownAbortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    dropdownAbortControllerRef.current = abortController;

    try {
      const sample = buildSamplePayload(currentInputValues, fieldList);
      const response = await getDropdownSuggestionsAndTests(
        sample,
        abortController.signal,
        PRODUCT_SAMPLE_CATEGORY,
      );

      if (abortController.signal.aborted || response.aborted) {
        return;
      }

      dropdownAbortControllerRef.current = null;

      if (response.success) {
        const { test_list, ...suggestionFields } = response.result;
        setDropdownSuggestions(suggestionFields);
      }
    } catch (error) {
      toast.error(error.message || "Failed to load dropdown suggestions");
    }
  };

  const handleInputValuesChange = (nextInputValues) => {
    setInputValues(nextInputValues);
    if (debouncedFetchDropdownsRef.current) {
      debouncedFetchDropdownsRef.current(nextInputValues);
    }
    if (debouncedGenerateSampleIdRef.current) {
      debouncedGenerateSampleIdRef.current(nextInputValues);
    }
  };

  const setFieldValue = (field, value) => {
    const nextInputValues = {
      ...inputValues,
      [field]: value,
    };
    handleInputValuesChange(nextInputValues);
  };

  const handleSave = async () => {
    if (debouncedGenerateSampleIdRef.current) {
      debouncedGenerateSampleIdRef.current.cancel();
    }
    if (debouncedFetchDropdownsRef.current) {
      debouncedFetchDropdownsRef.current.cancel();
    }

    const hasMandatoryMissing = sampleFields.some((field) => {
      if (sampleFieldsInfo[field].mandatory !== "1") {
        return false;
      }
      return !(inputValues[field] || "").trim();
    });

    if (hasMandatoryMissing) {
      toast.error("Please fill all mandatory fields");
      return;
    }

    const hasInvalidInput = Object.values(inputInvalid).some(Boolean);
    if (hasInvalidInput) {
      toast.error("Please fix invalid field values");
      return;
    }

    const hasAtLeastOneSampleIdField = sampleFieldListRef.current.some((field) => {
      if (!sampleIdFields.has(field)) {
        return false;
      }
      return Boolean((inputValues[field] || "").trim());
    });

    if (!hasAtLeastOneSampleIdField) {
      toast.error("At least one Sample ID field is required");
      return;
    }

    setIsSaving(true);

    try {
      const generateResponse = await fetchGeneratedSampleId(inputValues, sampleFieldListRef.current);
      if (generateResponse.aborted) {
        return;
      }
      if (!generateResponse.success) {
        toast.error(generateResponse.message || "Unable to generate Sample ID");
        return;
      }

      const sampleIdToSave = generateResponse.sample_id;

      if (sampleIdToSave.length > CHAR_LIMITS.sample_id) {
        toast.error("Sample ID is too long");
        return;
      }

      const formattedReceivedDate = receivedDate
        ? moment(new Date(receivedDate)).format("YYYY-MM-DD")
        : "";

      const customizedFields = buildCustomizedFieldsSnapshot(sampleFieldListRef.current, inputValues);

      const response = await saveSampleIdEditHistory({
        sample_id: liveSampleId,
        sample_type: sampleType,
        received_date: formattedReceivedDate,
        new_sample_id: sampleIdToSave,
        new_sample_type: sampleType,
        customized_fields: customizedFields,
      });

      if (response.success) {
        toast.success("Sample ID updated successfully");
        onSaveSuccess(sampleIdToSave);
        return;
      }

      toast.error(response.message || "Failed to save Sample ID edit");
    } catch (error) {
      toast.error(error.message || "Failed to save Sample ID edit");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    debouncedGenerateSampleIdRef.current = debounce((currentInputValues) => {
      fetchGeneratedSampleId(currentInputValues);
    }, 500);

    debouncedFetchDropdownsRef.current = debounce((currentInputValues) => {
      fetchDropdownSuggestions(currentInputValues);
    }, 500);

    return () => {
      if (debouncedGenerateSampleIdRef.current) {
        debouncedGenerateSampleIdRef.current.cancel();
      }
      if (debouncedFetchDropdownsRef.current) {
        debouncedFetchDropdownsRef.current.cancel();
      }
      if (dropdownAbortControllerRef.current) {
        dropdownAbortControllerRef.current.abort();
      }
      if (sampleIdAbortControllerRef.current) {
        sampleIdAbortControllerRef.current.abort();
      }
    };
  }, [liveSampleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isMounted = true;

    async function loadSampleFields() {
      setLoadingFields(true);

      try {
        const result = await getSampleFields(PRODUCT_SAMPLE_CATEGORY);

        if (!isMounted) {
          return;
        }

        if (!result.success || !result.fields.length) {
          toast.error(result.message || "Failed to load sample fields");
          onClose();
          return;
        }

        const fieldsMap = buildSampleFieldsInfo(result.fields);
        const submissionFields = result.fields
          .filter(({ display }) => display !== "2")
          .map(({ json_field }) => json_field);
        const sampleIdFieldSet = new Set(result.sampleid_fields);
        const sampleIdFieldList = submissionFields.filter((field) => sampleIdFieldSet.has(field));

        const prefillValues = buildPrefillValues(sampleIdFieldList, reportData, sampleIdEditHistory);
        const initialInputValues = initializeObjectWithDefaultValue(sampleIdFieldList, "");
        sampleIdFieldList.forEach((field) => {
          initialInputValues[field] = prefillValues[field];
        });

        setSampleFields(sampleIdFieldList);
        setSampleFieldsInfo(fieldsMap);
        setSampleIdFields(sampleIdFieldSet);
        setDelimiterRegex(result.delimiterRegex || "");
        setInputValues(initialInputValues);
        setInputInvalid(initializeObjectWithDefaultValue(sampleIdFieldList, false));
        setGeneratedSampleId(displayedSampleId);
        setSampleIdGenerationError(false);
        sampleFieldListRef.current = sampleIdFieldList;

        fetchDropdownSuggestions(initialInputValues, sampleIdFieldList);
        fetchGeneratedSampleId(initialInputValues, sampleIdFieldList);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        toast.error(error.message || "Failed to load sample fields");
        onClose();
      } finally {
        if (isMounted) {
          setLoadingFields(false);
        }
      }
    }

    loadSampleFields();

    return () => {
      isMounted = false;
    };
  }, [reportData, displayedSampleId, sampleIdEditHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidSampleIdLength = generatedSampleId.length > CHAR_LIMITS.sample_id;
  const showSampleIdError = invalidSampleIdLength || sampleIdGenerationError;

  let sampleIdTooltipMessage = "Sample ID is auto-generated by user inputted data";
  if (sampleIdGenerationError) {
    sampleIdTooltipMessage = "Failed to generate Sample ID";
  } else if (invalidSampleIdLength) {
    sampleIdTooltipMessage = "Sample ID is too long, please fix your inputs";
  }

  return (
    <Modal
      open
      closable={false}
      centered
      footer={null}
      onCancel={isSaving ? undefined : onClose}
      wrapClassName="EditSampleIdModal"
      className="EditSampleIdModal"
      width="743px"
      destroyOnClose
    >
      <div className="EditSampleIdModal__Header">
        <span>Edit Sample ID</span>
        <CloseOutlined
          className="EditSampleIdModal__CloseIcon"
          aria-label="Close"
          onClick={() => {
            if (!isSaving) {
              onClose();
            }
          }}
        />
      </div>

      <div className="EditSampleIdModal__Body">
        {loadingFields && (
          <div className="EditSampleIdModal__Loading">
            <LoadingDots width={28} height={28} fill="#19305A" />
          </div>
        )}

        {!loadingFields && (
          <>
            <div className="EditSampleIdModal__SampleIdSection">
              <div className="EditSampleIdModal__SampleIdCard">
                <div className="EditSampleIdModal__SampleIdTitleRow">
                  <label htmlFor="edit-sample-id-preview">Sample ID</label>
                </div>
                <StyledTooltip title={sampleIdTooltipMessage} placement="top">
                  <ExclamationIcon
                    className={`EditSampleIdModal__SampleIdErrorIcon${showSampleIdError ? "--error" : ""}`}
                    width={14}
                    height={14}
                  />
                </StyledTooltip>
                <div className="EditSampleIdModal__SampleIdInputWrap">
                  <Input
                    className="EditSampleIdModal__SampleIdInput"
                    status={showSampleIdError ? "error" : undefined}
                    id="edit-sample-id-preview"
                    value={generatedSampleId}
                    readOnly
                  />
                  {isLoadingSampleId && (
                    <div className="EditSampleIdModal__SampleIdLoading">
                      <LoadingDots width={20} height={20} fill="#19305A" />
                    </div>
                  )}
                  <span
                    className={`EditSampleIdModal__CharCount${invalidSampleIdLength ? "--over-limit" : ""}`}
                  >
                    {`${generatedSampleId.length}/${CHAR_LIMITS.sample_id}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="EditSampleIdModal__FieldsSection">
              <div className="EditSampleIdModal__SectionTitle">
                <span className="EditSampleIdModal__SectionTitleAccent" />
                <span>Sample Details</span>
              </div>

              <div className="EditSampleIdModal__FieldListParent">
                <StyledScrollbar className="EditSampleIdModal__FieldListScrollbar">
                  <div className="EditSampleIdModal__FieldList">
                    {sampleFields.map((field, index) => (
                      <SampleDetailsAutocompleteInput
                        key={field}
                        idx={index}
                        json_field={field}
                        sampleIdFields={sampleIdFields}
                        handleDeselectField={() => {}}
                        inputValuesRefs={inputValuesRefs}
                        setFieldValue={setFieldValue}
                        inputValuesSavedForNext={{}}
                        inputValues={inputValues}
                        setInputValuesRefs={setInputValuesRef}
                        toggleSaveForNext={() => {}}
                        sampleFieldsInfo={sampleFieldsInfo}
                        delimiterRegex={delimiterRegex}
                        setFieldInputInvalid={setFieldInputInvalid}
                        charLimit={CHAR_LIMITS[field]}
                        dropdownSuggestionsObj={dropdownSuggestions}
                        sanitizeDropdown={sanitizeDropdown}
                        hideSaveForNext
                        isCompositeSample={false}
                        inputInvalid={inputInvalid}
                      />
                    ))}
                  </div>
                </StyledScrollbar>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="EditSampleIdModal__Footer">
        <Button
          type="default"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={isSaving}
          disabled={loadingFields || isLoadingSampleId}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
}
